import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import lenderALogo from '../assets/lender-a.png';
import coins1 from '../assets/coins1.svg';

const Screen5: React.FC = () => {
  const navigate = useNavigate();
  const [isApplying, setIsApplying] = useState(false);
  const [applicationStatus, setApplicationStatus] = useState<'idle' | 'applying' | 'approved' | 'pending'>('idle');
  const [showToast, setShowToast] = useState<{ message: string; type: 'success' | 'info' | 'loading' } | null>(null);

  const handleDownloadPDF = () => {
    setShowToast({ message: 'PDF download started', type: 'success' });

    // Simulate PDF generation
    setTimeout(() => {
      const blob = new Blob(['PDF content would be here'], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'debt-consolidation-offer.pdf';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);

      setShowToast({ message: 'PDF downloaded successfully!', type: 'success' });
    }, 1500);

    // Hide toast after 3 seconds
    setTimeout(() => setShowToast(null), 3000);
  };

  const handleAutoApply = async () => {
    if (isApplying) return;

    setIsApplying(true);
    setApplicationStatus('applying');
    setShowToast({ message: 'Submitting your application...', type: 'loading' });

    // Simulate application process - always approve for demo
    setTimeout(() => {
      setApplicationStatus('approved');
      setShowToast({ message: 'Application Approved! 🎉', type: 'success' });
      // Navigate to next screen after approval
      setTimeout(() => navigate('/screen6'), 2000);
      setIsApplying(false);
    }, 3000);
  };

  const getButtonText = () => {
    switch (applicationStatus) {
      case 'applying':
        return 'Applying...';
      case 'approved':
        return 'Approved! ✓';
      case 'pending':
        return 'Under Review';
      default:
        return 'Auto-Apply';
    }
  };

  const getButtonColor = () => {
    switch (applicationStatus) {
      case 'approved':
        return 'bg-green-500';
      case 'pending':
        return 'bg-orange-500';
      default:
        return 'bg-[#9b86ea] hover:bg-[#8b76da]';
    }
  };

  // Decorative asset

  return (
    <motion.div
      className="bg-[#f0effa] h-[100dvh] w-full relative overflow-y-auto overscroll-contain"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.5 }}
    >
      {/* Toast Notification */}
      {showToast && (
        <motion.div
          className={`absolute top-[10px] z-50 px-[24px] py-[12px] rounded-[12px] shadow-lg ${
            showToast.type === 'success' ? 'bg-green-500' :
            showToast.type === 'info' ? 'bg-orange-500' :
            'bg-[#9b86ea]'
          } ${
            showToast.type === 'loading' || showToast.type === 'success'
              ? 'left-4 right-4 text-center'
              : 'left-1/2 -translate-x-1/2'
          }`}
          initial={{ y: -50, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: -50, opacity: 0 }}
        >
          <p className="text-white text-[14px] font-medium font-['Bricolage_Grotesque',_sans-serif]">
            {showToast.message}
          </p>
        </motion.div>
      )}

      {/* Title */}
      <motion.div
        className="absolute top-[64px] left-0 right-0 text-center"
        initial={{ y: -20, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ delay: 0.2 }}
      >
        <h1 className="text-[45px] font-medium text-black font-['Bricolage_Grotesque',_sans-serif] leading-[45px]">
          Final Details
        </h1>
      </motion.div>

      {/* Subheading */}
      <motion.p
        className="absolute top-[140px] left-1/2 -translate-x-1/2 w-[330px] text-center text-[15px] text-black font-['Bricolage_Grotesque',_sans-serif] px-[20px]"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.3 }}
      >
        This will save you $175/month &amp;<br />
        $3,297 in total interest and cashflow
      </motion.p>

      {/* Main Card */}
      <motion.div
        className="absolute top-[207px] left-[48px] w-[294px] h-[457px] bg-white rounded-[30px] shadow-[6px_6px_0px_0px_#221a51]"
        initial={{ scale: 0.8, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        transition={{ delay: 0.4, type: "spring" }}
        whileHover={{ y: -3, rotate: -0.4 }}
        whileTap={{ scale: 0.995 }}
      >
        {/* Lender Logo */}
        <img
          src={lenderALogo}
          alt="Lender A"
          className="absolute top-[30px] left-[29px] w-[49px] h-[47px] rounded-[12px] object-contain"
        />
        {/* Lender Name - vertically centered with logo */}
        <div className="absolute top-[30px] left-[92px] h-[47px] flex items-center">
          <p className="text-[18px] font-medium text-black font-['Bricolage_Grotesque',_sans-serif]">
            Lender A
          </p>
        </div>

        {/* Loan Details */}
        <div className="absolute top-[96px] left-[37px]">
          <p className="text-[15px] text-black font-normal font-['Bricolage_Grotesque',_sans-serif] leading-[22px]">
            Loan Amount: $25,000<br />
            Interest Rate: 12.55%<br />
            Loan Term: 60 months
          </p>
        </div>

        {/* Consolidating Debts */}
        <div className="absolute top-[191px] left-[37px] w-[223px]">
          <p className="text-[15px] text-black font-['Bricolage_Grotesque',_sans-serif] leading-[22px]">
            <span className="font-bold">Consolidating debts:</span><br />
            Avanti: $11,497 @ 25%<br />
            Instant Finance: $13,503 @32%
          </p>
        </div>

        {/* Savings Amount */}
        <p className="absolute top-[286px] left-[37px] text-[32px] font-medium text-black font-['Bricolage_Grotesque',_sans-serif]">
          $3,297
        </p>
        <p className="absolute top-[328px] left-[37px] text-[15px] text-black font-normal font-['Bricolage_Grotesque',_sans-serif]">
          potential savings
        </p>

        {/* Download PDF Button */}
        <button
          onClick={handleDownloadPDF}
          className="absolute bottom-[20px] left-[29px] w-[237px] bg-[#9b86ea] px-[24px] py-[12px] rounded-[50px] shadow-[4px_4px_0px_0px_#694ccc] flex items-center justify-center gap-[10px] hover:bg-[#8b76da] hover:shadow-[2px_2px_0px_0px_#694ccc] hover:translate-x-[2px] hover:translate-y-[2px] transition-all"
        >
          <span className="text-white text-[14px] font-semibold font-['Bricolage_Grotesque',_sans-serif]">
            Download offer as PDF
          </span>
        </button>

        {/* Decorative Coins */}
        <motion.img
          src={coins1}
          alt=""
          className="absolute right-[16px] top-[25%] -translate-y-1/2 w-[50px] h-[22px] object-contain"
          animate={{ rotate: [12, 16, 12], y: [0, -2, 0] }}
          transition={{ duration: 2, repeat: Infinity }}
        />
      </motion.div>

      {/* First Payment Info */}
      <motion.p
        className="absolute top-[685px] left-[105px] text-[15px] text-black font-['Bricolage_Grotesque',_sans-serif]"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.6 }}
      >
        First payment: In 4 weeks
      </motion.p>

      {/* Auto-Apply Button */}
      <motion.button
        onClick={handleAutoApply}
        disabled={isApplying || applicationStatus === 'approved'}
        className={`absolute top-[756px] left-[74px] w-[242px] ${getButtonColor()} px-[12px] py-[12px] rounded-[50px] shadow-[6px_8px_0px_0px_#221a51] flex items-center justify-center transition-colors disabled:cursor-not-allowed disabled:opacity-75`}
        initial={{ y: 50, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ delay: 0.7 }}
        whileTap={{ scale: 0.95 }}
      >
        <span className="text-white text-[20px] font-semibold font-['Bricolage_Grotesque',_sans-serif]">
          {getButtonText()}
        </span>
        {isApplying && (
          <div className="absolute right-4">
            <motion.div
              className="w-4 h-4 border-2 border-white border-t-transparent rounded-full"
              animate={{ rotate: 360 }}
              transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
            />
          </div>
        )}
      </motion.button>
    </motion.div>
  );
};

export default Screen5;
